/**
 * Log severity levels (lower = more verbose)
 * Inspired by Unreal Engine's verbosity system
 */
export enum LogLevel {
  /** Most verbose - useful for detailed debugging */
  Trace = 0,
  /** Debug information for development */
  Debug = 1,
  /** General information about system state */
  Info = 2,
  /** Potentially harmful situations */
  Warn = 3,
  /** Error events that might still allow the application to continue */
  Error = 4,
  /** Severe errors that will lead to application abort */
  Fatal = 5,
  /** Disable all logging */
  Off = 6,
}

/**
 * Built-in log categories (inspired by Unity/Unreal)
 * Can be extended with custom categories
 */
export type BuiltinLogCategory =
  | 'Engine'
  | 'Render'
  | 'Physics'
  | 'Audio'
  | 'Input'
  | 'Network'
  | 'AI'
  | 'UI'
  | 'Script'
  | 'Asset'
  | 'Scene'
  | 'Editor'
  | 'VFX'
  | 'Animation'
  | 'Memory';

export type LogCategory = BuiltinLogCategory | (string & {});

/**
 * Configuration for a specific log category
 */
export interface CategoryConfig {
  /** Whether this category is enabled */
  enabled: boolean;
  /** Minimum level for this category (overrides global) */
  level?: LogLevel;
  /** Custom color for DevTools output */
  color?: string;
}

/**
 * Global logger configuration
 */
export interface LoggerConfig {
  /** Global minimum log level */
  level: LogLevel;
  /** Enable/disable all logging */
  enabled: boolean;
  /** Show timestamps in log output */
  showTimestamp: boolean;
  /** Show category name in log output */
  showCategory: boolean;
  /** Show log level in output */
  showLevel: boolean;
  /** Use DevTools features when available */
  useDevTools: boolean;
  /** Enable styled console output (colors, badges) */
  useStyles: boolean;
  /** Include stack traces for errors */
  stackTraceOnError: boolean;
  /** Include stack traces for warnings */
  stackTraceOnWarn: boolean;
  /** Rate limit duplicate messages (ms, 0 to disable) */
  rateLimitMs: number;
  /** Per-category configuration */
  categories: Partial<Record<LogCategory, CategoryConfig>>;
}

/**
 * Log entry metadata
 */
export interface LogEntry {
  level: LogLevel;
  category: LogCategory;
  message: string;
  args: unknown[];
  timestamp: number;
  stackTrace?: string;
}

/**
 * Log output handler interface
 */
export interface LogHandler {
  handle(entry: LogEntry): void;
}

/**
 * Timer tracking for performance logging
 */
export interface TimerInfo {
  label: string;
  startTime: number;
  category: LogCategory;
}

/**
 * Assertion failure information
 */
export interface AssertionFailure {
  category: LogCategory;
  message: string;
  args: unknown[];
  timestamp: number;
  stackTrace?: string;
}

/**
 * Callback for assertion failures
 */
export type AssertionHandler = (failure: AssertionFailure) => void;

/**
 * Disposable group scope for use with `using` keyword
 */
export interface LogGroupScope extends Disposable {
  /** End the group manually */
  end(): void;
}

/**
 * Disposable timer scope for use with `using` keyword
 */
export interface LogTimerScope extends Disposable {
  /** Get elapsed time without stopping */
  elapsed(): number;
  /** Stop timer and return duration */
  stop(): number;
}

/**
 * Default colors for log categories (CSS color values)
 */
export const DEFAULT_CATEGORY_COLORS: Record<BuiltinLogCategory, string> = {
  Engine: '#6B7280',
  Render: '#3B82F6',
  Physics: '#10B981',
  Audio: '#8B5CF6',
  Input: '#F59E0B',
  Network: '#EF4444',
  AI: '#EC4899',
  UI: '#14B8A6',
  Script: '#F97316',
  Asset: '#84CC16',
  Scene: '#06B6D4',
  Editor: '#A855F7',
  VFX: '#FF6B6B',
  Animation: '#4ECDC4',
  Memory: '#FFE66D',
};

/**
 * Colors for log levels
 */
export const LOG_LEVEL_COLORS: Record<LogLevel, string> = {
  [LogLevel.Trace]: '#9CA3AF',
  [LogLevel.Debug]: '#60A5FA',
  [LogLevel.Info]: '#34D399',
  [LogLevel.Warn]: '#FBBF24',
  [LogLevel.Error]: '#F87171',
  [LogLevel.Fatal]: '#DC2626',
  [LogLevel.Off]: '#000000',
};

/**
 * Level name strings
 */
export const LOG_LEVEL_NAMES: Record<LogLevel, string> = {
  [LogLevel.Trace]: 'TRACE',
  [LogLevel.Debug]: 'DEBUG',
  [LogLevel.Info]: 'INFO',
  [LogLevel.Warn]: 'WARN',
  [LogLevel.Error]: 'ERROR',
  [LogLevel.Fatal]: 'FATAL',
  [LogLevel.Off]: 'OFF',
};
